import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { store, RootState } from "../../state";
import { GiHamburgerMenu } from "react-icons/gi";
import SideNav from "./SideNav";
import logo from "../../assets/images/JaguarTCSRacingLogoWhite.png";
import { brandColours } from "../../assets/brandColours";
import TPMSDataManager from "../DataManagers/TPMSDataManager";
import TimingDataManager from "../DataManagers/TimingDataManager";
import { Tooltip } from "react-tooltip";
import { MutateUserViewingPage } from "../../utils/graphql/hooks/adminHooks";
import { connectionId } from "../..";
import Version from "./version";
import runTimeVars from "../../runTimeVars";

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  title: string;
  configOptions?: React.ReactNode;
  barColour?: string;
  showJaguarLogo?: boolean;
  showNavBar?: boolean;
  showVersion?: boolean;
}

function AuthenticatedLayout(props: AuthenticatedLayoutProps) {
  const {
    children,
    title,
    configOptions = <></>,
    barColour = brandColours.gold,
    showJaguarLogo = true,
    showNavBar = true,
    showVersion = false,
  } = props;

  const wsConnected = useSelector(
    (state: RootState) => state.admin.wsConnected,
  );
  const v2Connected = useSelector(
    (state: RootState) => state.aks.v2ConnectedState.connected,
  );
  const tpmsReceiverConnected = useSelector(
    (state: RootState) => state.tpms.tpmsReceiverConnected,
  );
  const [showNav, toggleNav] = useState(false);

  const userViewingPage = MutateUserViewingPage();

  const username = store.getState().permissions.username;
  if (!username && runTimeVars.enableMsalAuth !== "false") {
    window.location.replace("/login");
  }

  useEffect(() => {
    console.log(`Websocket connection status changed: ${wsConnected}`);
    if (wsConnected) {
      userViewingPage?.({
        variables: {
          page: title,
          connectionId,
          status: true,
        },
      });
    }
  }, [wsConnected]);

  useEffect(() => {
    return () => {
      userViewingPage?.({
        variables: {
          page: title,
          connectionId,
          status: false,
        },
      });
    };
  }, []);

  function containsComponentType(
    children: React.ReactNode,
    componentType: React.ElementType,
  ): boolean {
    return React.Children.toArray(children).some((child) => {
      if (React.isValidElement(child)) {
        // Check direct match
        if (child.type === componentType) {
          return true;
        }
        // Check recursively in children prop
        if (child.props && child.props.children) {
          return containsComponentType(child.props.children, componentType);
        }
      }
      return false;
    });
  }

  const hasTPMSDataManager = containsComponentType(children, TPMSDataManager);
  const hasTimingDataManager = containsComponentType(
    children,
    TimingDataManager,
  );

  return (
    <>
      <Tooltip id="backendVersionTooltip" />
      <div className={styles.container}>
        <div className={styles.header} style={{ backgroundColor: barColour }}>
          {showNavBar ? (
            <GiHamburgerMenu
              className={styles.hamburger}
              onClick={() => toggleNav(true)}
            ></GiHamburgerMenu>
          ) : null}
          <div className={styles.title}>{title}</div>
          {configOptions}
          {showVersion && <Version />}
        </div>
        <SideNav showNav={showNav} toggleNav={toggleNav} />
        <div className={styles.children}>
          {children}
          {showJaguarLogo && (
            <div className={styles.jaguarLogo}>
              <img src={logo} alt="Jaguar TCS Racing Logo"></img>
            </div>
          )}
        </div>
        <div
          className={`${styles.footer} ${
            (hasTPMSDataManager && !tpmsReceiverConnected) ||
            (hasTimingDataManager && !v2Connected)
              ? styles.bannerWarning
              : null
          }`}
          style={{ backgroundColor: brandColours.gold }}
        >
          <div className={styles.bannerText}>
            {hasTPMSDataManager && !tpmsReceiverConnected
              ? "GARAGE MONITOR DISCONNECTED"
              : hasTimingDataManager && !v2Connected
              ? "TIMING FEED DISCONNECTED"
              : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthenticatedLayout;
